import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { MapLegendEntry } from '@/lib/api/map';
import { isTourismDesignationLabel } from '@/lib/map/compilation-layer-def';

type MapDesignationFilterContextValue = {
  entries: MapLegendEntry[];
  registerEntries: (items: MapLegendEntry[]) => void;
  enabledCodes: Set<string>;
  toggleCode: (code: string) => void;
  setEnabledCodes: (codes: Iterable<string>) => void;
  selectAll: () => void;
  selectNone: () => void;
  selectTourismOnly: () => void;
  enabledCount: number;
  isMapFiltered: boolean;
};

const MapDesignationFilterContext = createContext<MapDesignationFilterContextValue | null>(
  null,
);

export function MapDesignationFilterProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<MapLegendEntry[]>([]);
  const [enabledCodes, setEnabledCodesState] = useState<Set<string>>(new Set());

  const registerEntries = useCallback((items: MapLegendEntry[]) => {
    const codes = items.map((e) => e.code).filter(Boolean);
    setEntries(items);
    setEnabledCodesState((prev) => {
      if (prev.size === 0) {
        return new Set(codes);
      }
      const valid = new Set(codes);
      const kept = [...prev].filter((code) => valid.has(code));
      return kept.length > 0 ? new Set(kept) : new Set(codes);
    });
  }, []);

  const allCodes = useMemo(
    () => entries.map((e) => e.code).filter(Boolean),
    [entries],
  );

  const toggleCode = useCallback((code: string) => {
    setEnabledCodesState((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  }, []);

  const setEnabledCodes = useCallback((codes: Iterable<string>) => {
    setEnabledCodesState(new Set(codes));
  }, []);

  const selectAll = useCallback(() => {
    setEnabledCodesState(new Set(allCodes));
  }, [allCodes]);

  const selectNone = useCallback(() => {
    setEnabledCodesState(new Set());
  }, []);

  const selectTourismOnly = useCallback(() => {
    const greenFromLegend = entries
      .filter((e) => e.traffic_light === 'green')
      .map((e) => e.code)
      .filter(Boolean);
    const tourism =
      greenFromLegend.length > 0
        ? greenFromLegend
        : entries
            .filter((e) => isTourismDesignationLabel(e.label))
            .map((e) => e.code)
            .filter(Boolean);
    setEnabledCodesState(new Set(tourism.length > 0 ? tourism : allCodes));
  }, [entries, allCodes]);

  const enabledCount = enabledCodes.size;
  const isMapFiltered =
    allCodes.length > 0 &&
    (enabledCount === 0 || enabledCount < allCodes.length);

  const value = useMemo(
    () => ({
      entries,
      registerEntries,
      enabledCodes,
      toggleCode,
      setEnabledCodes,
      selectAll,
      selectNone,
      selectTourismOnly,
      enabledCount,
      isMapFiltered,
    }),
    [
      entries,
      registerEntries,
      enabledCodes,
      toggleCode,
      setEnabledCodes,
      selectAll,
      selectNone,
      selectTourismOnly,
      enabledCount,
      isMapFiltered,
    ],
  );

  return (
    <MapDesignationFilterContext.Provider value={value}>
      {children}
    </MapDesignationFilterContext.Provider>
  );
}

export function useMapDesignationFilter(): MapDesignationFilterContextValue {
  const ctx = useContext(MapDesignationFilterContext);
  if (!ctx) {
    throw new Error('useMapDesignationFilter must be used within MapDesignationFilterProvider');
  }
  return ctx;
}
